import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { CubicData, KPICardData } from "store/types";

interface Props {
  data?: KPICardData;
  loading?: boolean;
}

export const KpiCard: React.FC<Props> = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const { widthPercent } = useCardSize();

  const cubics: CubicData[] = [];

  for (let i = 1; i <= 5; i++) {
    const key = `cubic${i}` as keyof KPICardData;
    const cubic = data[key];
    if (cubic) cubics.push(cubic);
  }

  if (cubics.length === 0) {
    return (
      <Box sx={{ p: "20px 24px" }}>
        {loading && <LinearProgress sx={{ mb: "4px" }} />}
        <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No KPI data available</Typography>
      </Box>
    );
  }

  const isCompact = widthPercent <= 50;
  const cols = isCompact ? Math.min(cubics.length, 2) : Math.min(cubics.length, 4);
  const density = widthPercent / cols;
  const fontSize = density >= 50 ? 28 : density >= 33 ? 22 : density >= 20 ? 18 : 15;
  const padding = density >= 50 ? "20px 24px" : density >= 33 ? "16px 18px" : "12px 14px";
  const gap = density >= 33 ? "8px 20px" : "6px 12px";
  const labelSize = isCompact ? 9 : 10;

  return (
    <Box sx={{ p: padding, height: "100%", display: "flex", flexDirection: "column" }}>
      {loading && <LinearProgress sx={{ mb: "4px" }} />}
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap, flex: 1, alignContent: "start" }}>
        {cubics.map((cubic, index) => (
          <Box key={index} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: labelSize,
                fontWeight: 600,
                color: "#496682",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: "4px",
                wordBreak: "break-word"
              }}
            >
              {cubic.title || cubic.name}
            </Typography>
            <Typography
              sx={{
                fontSize,
                fontWeight: 700,
                color: "#071f35",
                lineHeight: 1.1,
                wordBreak: "break-word"
              }}
            >
              {cubic.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
